import { Map as MlMap, addProtocol, type ExpressionSpecification, type FilterSpecification, type GeoJSONSource, type MapLayerMouseEvent } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { Protocol } from "pmtiles";
import type { ReplayEvent } from "../packages/db/src/public-types";
import { KIND_COLOUR, SECTION_COLOUR } from "./palette";
import { PulseLayer } from "./pulses";
import { LiveFeed } from "./live";
import { Replay } from "./replay";
import { mapStyle } from "./style";
import { buildFilters, closeCard, countReplayed, filters, matches, openCard, renderStats, tick, type Stats } from "./ui";

const UK: [[number, number], [number, number]] = [[-8.2, 49.9], [1.8, 58.7]];
const SETTLED_MAX = 20_000; // a busy day is ~4–5k plottable births + deaths; keep the whole day
const og = new URLSearchParams(location.search).has("og");
if (og) document.body.classList.add("og");
// Poster mode (share image) settles every dot at once, like reduced motion.
const reducedMotion = og || matchMedia("(prefers-reduced-motion: reduce)").matches;

const protocol = new Protocol();
addProtocol("pmtiles", protocol.tile);

const map = new MlMap({
  container: "map",
  style: mapStyle(`${location.origin}/tiles/uk.pmtiles`),
  center: [-3.4, 54.6],
  zoom: 5,
  maxBounds: [[-16, 47.5], [8, 62.5]],
  minZoom: 4,
  maxZoom: 15,
  dragRotate: false,
  pitchWithRotate: false,
  attributionControl: { compact: true },
});
map.touchZoomRotate.disableRotation();
// Fit the UK between the header and the filter bar. Padding is clamped to the canvas so narrow or short
// viewports never hit "cannot fit within canvas" (which left the map unpositioned on phones).
function fitUk(): void {
  // Poster mode is always 1200×630: a fixed view (Mercator-fitted for 49.9°–58.8°N) frames the whole UK.
  // maxBounds would clamp this wide view (and silently zoom in), so the poster lifts it.
  if (og) { map.setMaxBounds(null); map.jumpTo({ center: [-2.4, 54.3], zoom: 4.9 }); return; }
  const el = map.getContainer(), w = el.clientWidth, h = el.clientHeight;
  const mobile = w < 760;
  const top = og ? 20 : Math.min(mobile ? 200 : 100, h * 0.3), bottom = og ? 10 : Math.min(mobile ? 170 : 120, h * 0.3);
  try { map.fitBounds(UK, { padding: { top, bottom, left: Math.min(20, w * 0.05), right: Math.min(20, w * 0.05) }, duration: 0 }); }
  catch { map.jumpTo({ center: [-3.4, 54.6], zoom: mobile ? 4.4 : 5 }); }
}
fitUk();
map.on("error", (e) => console.error("[map]", e.error?.message ?? e));
(globalThis as { ltdMap?: MlMap }).ltdMap = map; // handy for debugging from the console

/* ---------- settled dots (GeoJSON) ---------- */

interface DotFeature { type: "Feature"; geometry: { type: "Point"; coordinates: [number, number] }; properties: Record<string, string> }
const settled: DotFeature[] = [];
/** kind:number of every settled dot, so today's preload and the replay/live feed never double up. */
const seen = new Set<string>();
let dirty = false;

function settle(ev: Pick<ReplayEvent, "kind" | "lng" | "lat" | "section" | "region" | "number">): void {
  const key = `${ev.kind}:${ev.number}`;
  if (seen.has(key)) return;
  seen.add(key);
  settled.push({
    type: "Feature",
    geometry: { type: "Point", coordinates: [ev.lng, ev.lat] },
    properties: { kind: ev.kind, section: ev.section, region: ev.region ?? "", number: ev.number },
  });
  if (settled.length > SETTLED_MAX) for (const f of settled.splice(0, settled.length - SETTLED_MAX)) seen.delete(`${f.properties.kind}:${f.properties.number}`);
  if (!dirty) {
    dirty = true;
    requestAnimationFrame(() => {
      dirty = false;
      (map.getSource("settled") as GeoJSONSource | undefined)?.setData({ type: "FeatureCollection", features: settled });
    });
  }
}

function applyFilters(): void {
  const f: FilterSpecification = [
    "all",
    ["in", ["get", "kind"], ["literal", [...filters.kinds]]],
    filters.sections.size ? ["in", ["get", "section"], ["literal", [...filters.sections]]] : true,
    filters.region ? ["==", ["get", "region"], filters.region] : true,
  ];
  for (const id of ["settled", "settled-glow"]) if (map.getLayer(id)) map.setFilter(id, f);
  applyColours();
}

/* ---------- dot colours ----------
 * All sectors: births green, deaths red (the hero colours).
 * Sector chips picked: every dot takes its sector's chip colour; births solid, deaths hollow rings, so both stay readable. */
const bySector = (): boolean => filters.sections.size > 0;
const sectorColour: ExpressionSpecification = ["match", ["get", "section"], ...Object.entries(SECTION_COLOUR).flat(), SECTION_COLOUR.X!] as unknown as ExpressionSpecification;
const kindColour: ExpressionSpecification = ["match", ["get", "kind"], "birth", KIND_COLOUR.birth, KIND_COLOUR.death];

function applyColours(): void {
  if (!map.getLayer("settled")) return;
  const colour = bySector() ? sectorColour : kindColour;
  map.setPaintProperty("settled-glow", "circle-color", colour);
  map.setPaintProperty("settled", "circle-color", bySector() ? ["match", ["get", "kind"], "birth", sectorColour, "rgba(0,0,0,0)"] as unknown as ExpressionSpecification : kindColour);
  map.setPaintProperty("settled", "circle-stroke-color", colour);
  map.setPaintProperty("settled", "circle-stroke-width", bySector() ? ["match", ["get", "kind"], "birth", 0, 1.3] as unknown as ExpressionSpecification : 0);
  map.setPaintProperty("settled", "circle-stroke-opacity", 0.9);
  map.setPaintProperty("settled", "circle-opacity", bySector() ? 0.9 : ["match", ["get", "kind"], "birth", 0.85, 0.6] as unknown as ExpressionSpecification);
}

map.on("load", () => {
  performance.mark("ltd:map-load");
  // Replay starts before the basemap finishes loading, so dots that already arrived are in `settled`.
  map.addSource("settled", { type: "geojson", data: { type: "FeatureCollection", features: settled } });
  map.addLayer({
    id: "settled-glow",
    type: "circle",
    source: "settled",
    paint: {
      "circle-color": ["match", ["get", "kind"], "birth", KIND_COLOUR.birth, KIND_COLOUR.death],
      "circle-radius": ["interpolate", ["linear"], ["zoom"], 5, 5, 9, 9, 13, 14],
      "circle-opacity": 0.12,
      "circle-blur": 1,
    },
  });
  map.addLayer({
    id: "settled",
    type: "circle",
    source: "settled",
    paint: {
      "circle-color": ["match", ["get", "kind"], "birth", KIND_COLOUR.birth, KIND_COLOUR.death],
      "circle-radius": ["interpolate", ["linear"], ["zoom"], 5, 2.1, 9, 3.2, 13, 5],
      "circle-opacity": ["match", ["get", "kind"], "birth", 0.85, 0.6],
      "circle-blur": 0.25,
    },
  });
  map.on("mouseenter", "settled", () => (map.getCanvas().style.cursor = "pointer"));
  map.on("mouseleave", "settled", () => (map.getCanvas().style.cursor = ""));
  map.on("click", "settled", (e: MapLayerMouseEvent) => {
    const n = e.features?.[0]?.properties?.number;
    if (typeof n === "string") void openCard(n);
  });
  applyFilters();
  applyColours();
  if (og) map.once("idle", () => setTimeout(() => document.body.classList.add("og-ready"), 1500));
  // Attribution starts expanded; collapse it so it doesn't sit over the map.
  map.getContainer().querySelector(".maplibregl-ctrl-attrib")?.classList.remove("maplibregl-compact-show");
});
void startReplay();

/* ---------- heatmap ---------- */

let heatLoaded = false;
async function toggleHeat(on: boolean): Promise<void> {
  if (on && !heatLoaded) {
    heatLoaded = true;
    const { cells } = (await (await fetch("/api/heat")).json()) as { cells: Array<[number, number, number]> };
    map.addSource("heat", {
      type: "geojson",
      data: { type: "FeatureCollection", features: cells.map(([lng, lat, n]) => ({ type: "Feature", geometry: { type: "Point", coordinates: [lng, lat] }, properties: { n } })) },
    });
    map.addLayer({
      id: "heat",
      type: "heatmap",
      source: "heat",
      paint: {
        "heatmap-weight": ["interpolate", ["linear"], ["get", "n"], 1, 0.15, 40, 1],
        "heatmap-intensity": ["interpolate", ["linear"], ["zoom"], 5, 0.6, 11, 2],
        "heatmap-radius": ["interpolate", ["linear"], ["zoom"], 5, 6, 11, 22],
        "heatmap-opacity": 0.75,
        "heatmap-color": ["interpolate", ["linear"], ["heatmap-density"],
          0, "rgba(0,0,0,0)", 0.2, "rgba(20,90,70,0.5)", 0.5, "#1fa37a", 0.8, "#34e89e", 1, "#d8ffe9"],
      },
    }, "settled");
  }
  if (map.getLayer("heat")) map.setLayoutProperty("heat", "visibility", on ? "visible" : "none");
}

/* ---------- replay ---------- */

const pulses = new PulseLayer(map);
let replaying = false;

let streaming = false;

async function refreshStats(): Promise<void> {
  const res = await fetch("/api/stats/today").catch(() => null);
  if (res?.ok) renderStats((await res.json()) as Stats, replaying);
  // Phase 7: with the Companies House stream connected, dots arrive within about a minute.
  if (streaming) document.getElementById("c-updated")!.textContent = "Live";
}

function onEvent(ev: ReplayEvent, animate: boolean): void {
  if (replaying) countReplayed(ev.kind);
  if (!matches(ev)) return settle(ev);
  if (animate && !reducedMotion) {
    tick(ev, (n) => void openCard(n));
    pulses.add({ lng: ev.lng, lat: ev.lat, kind: ev.kind, then: () => settle(ev), ...(bySector() ? { colour: SECTION_COLOUR[ev.section] ?? SECTION_COLOUR.X } : {}) });
  } else settle(ev);
}

/** Today's births and deaths as settled dots, so the map matches the "Today" counters (not just the last hour). */
async function loadToday(): Promise<void> {
  const res = await fetch("/api/dots/today").catch(() => null);
  if (!res?.ok) return;
  const { dots } = (await res.json()) as { dots: Array<[number, number, 0 | 1, string, string, string]> };
  for (const [lng, lat, k, section, region, number] of dots) settle({ kind: k ? "birth" : "death", lng, lat, section, region, number });
}

async function startReplay(): Promise<void> {
  await loadToday();
  // Live stream first; fall back to the 15-minute replay if it isn't connected (or drops later).
  const live = new LiveFeed({ onEvent, onDrop: () => { streaming = false; void runReplay(); } });
  streaming = await live.start().catch(() => false);
  if (streaming) {
    replaying = false;
    void refreshStats();
  } else await runReplay();
  setInterval(() => void refreshStats(), 60_000);
}

async function runReplay(): Promise<void> {
  await new Replay({
    onSlot(res) {
      // A quiet-period replay still has real counters for today; only the archive replay hides them.
      replaying = res.replay && !res.quiet;
      document.body.classList.toggle("is-quiet", Boolean(res.quiet));
      const note = document.querySelector(".replay-banner .note");
      if (note && res.note) note.textContent = res.note;
      void refreshStats();
    },
    onEvent,
  }).start();
}

/* ---------- controls ---------- */

buildFilters(applyFilters);
document.getElementById("heat-toggle")!.addEventListener("click", (e) => {
  const b = e.currentTarget as HTMLButtonElement;
  const on = b.getAttribute("aria-pressed") !== "true";
  b.setAttribute("aria-pressed", String(on));
  void toggleHeat(on);
});
document.getElementById("card-close")!.addEventListener("click", closeCard);
document.addEventListener("keydown", (e) => { if (e.key === "Escape") closeCard(); });
